import { Box, Divider } from '@mui/material';
import { useCallback, useState, useEffect } from 'react';
import { Button, P1R, Dropzone, Icon, Loader, ActionForbiddenPopup } from 'src/components';
import Papa from 'papaparse';
import { useDispatch, useSelector } from 'react-redux';
import { bulkUploadUsers } from 'src/redux/users/actions';
import { selectCurrTenantId } from 'src/redux/tenants/selectors';
import { SharedRoleSlug, TenantRoleWithSuperSlug } from 'src/shared/enums/roleSlug.enum';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import Snackbar from 'src/components/Snackbar';
import { FileRejection } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'src/navigation';
import { loadOptions } from 'src/redux/options/actions';
import { bulkUploadUsersLoading, bulkUploadUsersSuccess } from 'src/redux/users/selectors';
import { useTranslation } from 'react-i18next';
import { DashboardWrapper } from '../wrappers';
import { validateUsersCsvRows } from './validateUsersCsv';
import ErrorReport from './components/ErrorReport';
import UploadedFileInfo from './components/UploadedFileInfo';

const UsersUploadContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tenantId = useSelector(selectCurrTenantId);
  const isLoading = useSelector(bulkUploadUsersLoading);
  const isSuccess = useSelector(bulkUploadUsersSuccess);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [validUsers, setValidUsers] = useState<any[]>([]);
  const [invalidUsers, setInvalidUsers] = useState<any[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, title: '', message: '', severity: 'error' as 'error' | 'success' | 'info' | 'warning' });
  const [viewType, setViewType] = useState<'errors' | 'table'>('errors');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showForbiddenPopup, setShowForbiddenPopup] = useState(false);

  useEffect(() => {
    if (isSuccess && !isTransitioning) {
      setIsTransitioning(true);
      setErrors({});
      setValidUsers([]);
      setInvalidUsers([]);
      setUploadedFile(null);
      setViewType('errors');

      dispatch(bulkUploadUsers.reset());

      navigate(PATHS.MY_USERS_PAGE);
      dispatch(loadOptions.init());
    }
  }, [isSuccess, navigate, dispatch, isTransitioning]);

  const handleDownload = () => {
    // const link = document.createElement('a');
    // link.href = `${process.env.PUBLIC_URL}/excel/users_bulk_upload_template.xlsx`;
    // link.download = 'users_bulk_upload_template.xlsx';
    // document.body.appendChild(link);
    // link.click();
    // document.body.removeChild(link);
    const templateLink = 'https://docs.google.com/spreadsheets/d/19EYVw1Lm0UXA6fms3KK6bl_OXgmwkuXgvq7E0Ur6LPU/edit?usp=sharing';
    window.open(templateLink, '_blank');
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    setUploadedFile(file);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // Check if file has data
        if (!results.data || results.data.length === 0) {
          setShowForbiddenPopup(true);
          return;
        }

        // Check required columns
        const requiredColumns = ['email(*)', 'role(*)', 'first_name(*)', 'last_name(*)'];
        const missingColumns = requiredColumns.filter((col) => !results.meta?.fields?.includes(col));

        if (missingColumns.length > 0) {
          setShowForbiddenPopup(true);
          return;
        }

        const { validUsers, invalidUsers, rowErrors } = validateUsersCsvRows(results.data, results.meta?.fields || []);
        setErrors(rowErrors);
        setValidUsers(validUsers);
        setInvalidUsers(invalidUsers);
      },
    });
  }, []);

  const handleFileRejectionsChange = useCallback(
    (fileRejections: FileRejection[]) => {
      if (fileRejections && fileRejections.length > 0) {
        const rejection = fileRejections[0];
        const fileName = rejection.file?.name || 'Unknown file';
        const errorCodes = rejection.errors.map((e) => e.code);
        if (errorCodes.includes('file-too-large')) {
          setSnackbar({
            open: true,
            title: t('bulkUpload.errors.tooBigFile.title'),
            message: t('bulkUpload.errors.tooBigFile.message'),
            severity: 'error',
          });
        } else if (errorCodes.includes('file-invalid-type')) {
          setSnackbar({
            open: true,
            title: t('bulkUpload.errors.wrongFormat.title'),
            message: t('bulkUpload.errors.wrongFormat.message'),
            severity: 'error',
          });
        } else {
          setSnackbar({
            open: true,
            title: t('bulkUpload.errors.uploadError.title'),
            message: t('bulkUpload.errors.uploadError.message', { fileName }),
            severity: 'error',
          });
        }
      } else {
        setSnackbar({ open: false, title: '', message: '', severity: 'error' });
      }
    },
    [t],
  );

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setErrors({});
    setValidUsers([]);
    setInvalidUsers([]);
  };

  const totalUsers = validUsers.length + Object.keys(errors).length;
  const errorCount = Object.keys(errors).length;

  const handleProceedWithValidUsers = () => {
    const sharedUsers = validUsers.filter((user) => Object.values(SharedRoleSlug).includes(user.role));
    const tenantUsers = validUsers.filter((user) => Object.values(TenantRoleWithSuperSlug).includes(user.role));

    dispatch(
      bulkUploadUsers.init({
        tenantId,
        tenantUsers,
        sharedUsers,
      }),
    );
  };

  return (
    <DashboardWrapper breadcrumbs={[[t('bulkUpload.title')]]}>
      <Box component="main" sx={{ height: 'calc(100vh - 100px)', pt: 2 }}>
        {isLoading || isTransitioning ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Loader />
          </Box>
        ) : (
          !isSuccess && (
            <>
              {!uploadedFile && (
                <>
                  <P1R component="p" sx={{ mb: 2 }}>
                    {t('bulkUpload.description')}
                  </P1R>
                  <P1R component="p" sx={{ mb: 4 }}>
                    {t('bulkUpload.largeFileWarning')}
                  </P1R>
                  <Button onClick={handleDownload} endIcon={<Icon name="downloadCSV" fontSize="small" />} sx={{ textTransform: 'capitalize', mb: 4 }}>
                    {t('bulkUpload.template')}
                  </Button>
                  <Dropzone
                    onDrop={onDrop}
                    onFileRejectionsChange={handleFileRejectionsChange}
                    maxFiles={1}
                    maxSize={50 * 1024 * 1024}
                    accept={{
                      'text/csv': ['.csv'],
                    }}
                    clickToSelectText={t('bulkUpload.clickToSelectText')}
                  />
                </>
              )}
              {uploadedFile && (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box>
                    <P1R component="p" sx={{ mb: 2 }}>
                      {t('bulkUpload.uploadSuccess.description')}
                    </P1R>
                    <P1R component="p" sx={{ mb: 4 }}>
                      {t('bulkUpload.uploadSuccess.duplicateWarning')}
                    </P1R>
                  </Box>

                  <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <UploadedFileInfo fileName={uploadedFile.name} errorCount={errorCount} totalUsers={totalUsers} onRemove={handleRemoveFile} />
                    <ErrorReport invalidUsers={invalidUsers} errors={errors} viewType={viewType} onViewTypeChange={setViewType} />
                  </Box>

                  <Divider sx={{ mb: 4, mt: 4 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 0 }}>
                    <Button primary={false} sx={{ textTransform: 'capitalize' }} startIcon={<CloudUploadOutlinedIcon />} onClick={handleRemoveFile}>
                      {t('bulkUpload.actions.uploadNewFile')}
                    </Button>
                    <Button onClick={handleProceedWithValidUsers} sx={{ textTransform: 'capitalize' }}>
                      {t('bulkUpload.actions.proceedWithValidUsers')}
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )
        )}
        <Snackbar
          open={snackbar.open}
          title={snackbar.title}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
        {showForbiddenPopup && (
          <ActionForbiddenPopup
            title={t('bulkUpload.errors.invalidData.title')}
            errorMessage={t('bulkUpload.errors.invalidData.message')}
            onClose={() => {
              setShowForbiddenPopup(false);
              handleRemoveFile();
            }}
          />
        )}
      </Box>
    </DashboardWrapper>
  );
};

export default UsersUploadContainer;
